// Linked entities in recruiter Q&A answers ("דבר עם המערכת").
//
// The server resolves and VALIDATES which applicants / positions / offers an
// answer refers to and which exact text occurrences may be links
// (row.entities + row.answer_segments / row.answer_long_segments, see
// RecruiterQa::EntityLinker). The client only renders: segments with an
// entity_key become clickable, everything else stays plain text, and entities
// never named inline are listed under the answer. Old payloads without these
// fields render as plain text.

export const ENTITY_TYPES = ['applicant', 'position', 'offer']

export const ENTITY_TYPE_LABELS = {
  applicant: 'מועמד/ת',
  position: 'משרה',
  offer: 'הצעה'
}

/** Server entities of a row, filtered to the shapes this module understands. */
export const rowEntities = row => {
  const list = row && Array.isArray(row.entities) ? row.entities : []
  return list.filter(e => e && ENTITY_TYPES.indexOf(e.type) !== -1 && parseInt(e.id, 10) > 0)
}

export const entityKey = e => String(e.key || (e.type + ':' + e.id))

const entityMap = entities => {
  const map = {}
  ;(entities || []).forEach(e => { map[entityKey(e)] = e })
  return map
}

/**
 * Segments of one text field: [{ text, entity|null }]. Uses the server
 * segments only when they reproduce the field exactly; otherwise plain text.
 */
export const rowSegments = (row, field) => {
  const text = String((row && row[field]) || '')
  if (!text) return []
  const raw = row[field + '_segments']
  const map = entityMap(rowEntities(row))
  if (Array.isArray(raw) && raw.length) {
    const ok = raw.every(s => s && typeof s.text === 'string')
    if (ok && raw.map(s => s.text).join('') === text) {
      return raw.map(s => ({ text: s.text, entity: (s.entity_key && map[s.entity_key]) || null }))
    }
  }
  return [{ text, entity: null }]
}

/** Entities linked nowhere in the row's answer texts (→ related list), deduplicated. */
export const unlinkedEntities = row => {
  const linked = {}
  ;['answer', 'answer_long'].forEach(f => rowSegments(row, f).forEach(s => { if (s.entity) linked[entityKey(s.entity)] = true }))
  const seen = {}
  return rowEntities(row).filter(e => {
    const k = entityKey(e)
    if (linked[k] || seen[k]) return false
    seen[k] = true
    return true
  })
}

/** Related-list text: "מועמד/ת: דבש מאגד", "משרה: מנהל עבודה · סולל בונה", "הצעה: דבש מאגד ← מנהל עבודה". */
export const entityChipLabel = entity => {
  const label = String(entity.label || '').trim() || ('#' + entity.id)
  return ENTITY_TYPE_LABELS[entity.type] + ': ' + label
}

/** Tooltip on an inline link. */
export const entityTitle = entity => {
  const label = String(entity.label || '').trim()
  const base = ENTITY_TYPE_LABELS[entity.type] + ' ' + entity.id
  return label ? base + ' · ' + label : base
}

/**
 * How a click opens the entity: the usual v_admin modal for applicants /
 * positions (same payloads LinkEntity.vue / IntakeAssistTasks.vue use), the
 * offers page in a NEW TAB for offers (no modal exists; the drawer keeps its state).
 */
export const entityOpenTarget = entity => {
  if (entity.type === 'applicant') {
    const first = String(entity.first_name || '').trim()
    const last = String(entity.last_name || '').trim()
    const fullName = [first, last].filter(Boolean).join(' ') || String(entity.label || '')
    const phone = String(entity.phone || '')
    return {
      kind: 'modal',
      modal: {
        designator: 'link-entity-resource-modal',
        resource_type: 'Applicant',
        resource_id: entity.id,
        component_name: 'ModalApplicant',
        data: { resource_type: 'Applicant', resource_id: entity.id, first_name: first, last_name: last, full_name: fullName, phone_number: phone },
        modal_title: [fullName, phone, entity.id].filter(Boolean).join(' | '),
        modal_width: 1500,
        is_center_modal: true,
        modal_open_action: 'click'
      }
    }
  }
  if (entity.type === 'position') {
    const title = String(entity.title || entity.label || '')
    return {
      kind: 'modal',
      modal: {
        designator: 'link-entity-resource-modal',
        resource_type: 'Position',
        resource_id: entity.id,
        component_name: 'ModalPosition',
        data: { id: entity.id, resource_type: 'Position', resource_id: entity.id, title, company: { name: String(entity.company_name || ''), resource_id: entity.company_id || null } },
        modal_title: [title, entity.company_name, entity.id].filter(Boolean).join(' | '),
        modal_width: 1500,
        is_center_modal: true,
        modal_open_action: 'click'
      }
    }
  }
  return { kind: 'tab', route: { name: 'admin-offers', query: { show_id: String(entity.id) } } }
}
